import React from 'react';
import { normalizeDutyStatus, type DutyStatusKey } from './utils';

interface CompactHosWidgetProps {
  currentDutyStatus: string | null;
  breakTimeRemainingMins: number | null;
  driveTimeRemainingMins: number | null;
  shiftTimeRemainingMins: number | null;
  cycleTimeRemainingMins: number | null;
  samsaraDriverId: string | null;
  /** When true, render as a single inline row of small text (used in ultra-compact lane header). */
  inline?: boolean;
}

// Max values in minutes for FMCSA 70-hour/8-day rule
const MAX_VALUES = {
  break: 8 * 60,    // 8 hours
  drive: 11 * 60,   // 11 hours
  shift: 14 * 60,   // 14 hours
  cycle: 70 * 60,   // 70 hours
};

// Format minutes to "H:MM" display format
function formatMinutesToTime(minutes: number | null): string {
  if (minutes === null || minutes === undefined) return '--:--';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}:${mins.toString().padStart(2, '0')}`;
}

// Badge presentation per canonical duty status. Classification lives in
// normalizeDutyStatus (shared with the Duty Status filter) so the badge a
// dispatcher sees always matches what the filter selects.
const DUTY_STATUS_BADGE: Record<DutyStatusKey, { abbr: string; label: string; color: string }> = {
  off_duty:  { abbr: 'OFF', label: 'Off Duty',  color: 'bg-gray-500' },
  sleeper:   { abbr: 'SB',  label: 'Sleeper',   color: 'bg-purple-500' },
  driving:   { abbr: 'D',   label: 'Driving',   color: 'bg-green-500' },
  on_duty:   { abbr: 'ON',  label: 'On Duty',   color: 'bg-blue-500' },
  yard_move: { abbr: 'YM',  label: 'Yard Move', color: 'bg-yellow-500' },
  personal:  { abbr: 'PC',  label: 'Personal',  color: 'bg-orange-500' },
};

// Get duty status abbreviation
function getDutyStatusAbbr(status: string | null): string {
  if (!status) return '--';
  const key = normalizeDutyStatus(status);
  return key ? DUTY_STATUS_BADGE[key].abbr : status.substring(0, 3).toUpperCase();
}

// Get duty status full label
function getDutyStatusLabel(status: string | null): string {
  if (!status) return '--';
  const key = normalizeDutyStatus(status);
  return key ? DUTY_STATUS_BADGE[key].label : status;
}

// Get duty status background color
function getDutyStatusColor(status: string | null): string {
  if (!status) return 'bg-gray-400';
  const key = normalizeDutyStatus(status);
  return key ? DUTY_STATUS_BADGE[key].color : 'bg-gray-400';
}

// Get progress bar color based on percentage remaining
function getProgressColor(percentage: number): string {
  if (percentage <= 0.25) return '#ef4444'; // red-500
  if (percentage <= 0.50) return '#f97316'; // orange-500
  if (percentage <= 0.75) return '#eab308'; // yellow-500
  return '#22c55e'; // green-500 (default)
}

interface CircularProgressProps {
  value: number;
  max: number;
  label: string;
  timeDisplay: string;
  size?: number;
}

const CircularProgress: React.FC<CircularProgressProps> = ({
  value,
  max,
  label,
  timeDisplay,
  size = 44
}) => {
  const strokeWidth = 3;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const percentage = Math.min(Math.max(value / max, 0), 1);
  const strokeDashoffset = circumference * (1 - percentage);
  const progressColor = getProgressColor(percentage);

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        {/* Background circle */}
        <svg className="absolute" width={size} height={size}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            className="text-gray-200 dark:text-gray-600"
          />
        </svg>
        {/* Progress circle */}
        <svg
          className="absolute transform -rotate-90"
          width={size}
          height={size}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={progressColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-300"
          />
        </svg>
        {/* Time text inside circle */}
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ fontSize: '10px', fontWeight: 600 }}
        >
          <span className="text-heading">{timeDisplay}</span>
        </div>
      </div>
      <span className="text-[0.625rem] text-muted mt-0.5">
        {label}
      </span>
    </div>
  );
};

export const CompactHosWidget: React.FC<CompactHosWidgetProps> = ({
  currentDutyStatus,
  breakTimeRemainingMins,
  driveTimeRemainingMins,
  shiftTimeRemainingMins,
  cycleTimeRemainingMins,
  samsaraDriverId,
  inline = false,
}) => {
  const hasAnyHosData = breakTimeRemainingMins !== null ||
                        driveTimeRemainingMins !== null ||
                        shiftTimeRemainingMins !== null ||
                        cycleTimeRemainingMins !== null ||
                        currentDutyStatus;

  if (!hasAnyHosData) {
    return null;
  }

  const dutyStatusAbbr = getDutyStatusAbbr(currentDutyStatus);
  const dutyStatusColor = getDutyStatusColor(currentDutyStatus);

  if (inline) {
    const segments: { label: string; value: number | null; max: number }[] = [
      { label: 'Break', value: breakTimeRemainingMins, max: MAX_VALUES.break },
      { label: 'Drive', value: driveTimeRemainingMins, max: MAX_VALUES.drive },
      { label: 'Shift', value: shiftTimeRemainingMins, max: MAX_VALUES.shift },
      { label: 'Cycle', value: cycleTimeRemainingMins, max: MAX_VALUES.cycle },
    ];
    return (
      <div className="flex items-center gap-1.5 text-[0.6875rem] text-gray-600 dark:text-gray-300">
        {currentDutyStatus && (
          samsaraDriverId ? (
            <a
              href={`https://cloud.samsara.com/o/39743/fleet/reports/hos/driver/${samsaraDriverId}`}
              target="_blank"
              rel="noopener noreferrer"
              title={`${getDutyStatusLabel(currentDutyStatus)} — view in Samsara`}
              className={`${dutyStatusColor} rounded px-1.5 py-0.5 text-white font-bold text-[0.625rem] leading-none hover:opacity-80 transition-opacity`}
            >
              {dutyStatusAbbr}
            </a>
          ) : (
            <span
              title={getDutyStatusLabel(currentDutyStatus)}
              className={`${dutyStatusColor} rounded px-1.5 py-0.5 text-white font-bold text-[0.625rem] leading-none`}
            >
              {dutyStatusAbbr}
            </span>
          )
        )}
        {segments.filter(s => s.value !== null).map((seg, i) => (
          <React.Fragment key={seg.label}>
            {i > 0 && <span className="text-gray-300 dark:text-gray-600">|</span>}
            <span>
              <span className="text-muted">{seg.label}:</span>{' '}
              <span className="font-semibold" style={{ color: getProgressColor(Math.min(Math.max(seg.value! / seg.max, 0), 1)) }}>
                {formatMinutesToTime(seg.value!)}
              </span>
            </span>
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {/* Duty Status Badge */}
      {currentDutyStatus && (
        <div className="flex flex-col items-center">
          {samsaraDriverId ? (
            <a
              href={`https://cloud.samsara.com/o/39743/fleet/reports/hos/driver/${samsaraDriverId}`}
              target="_blank"
              rel="noopener noreferrer"
              title="View in Samsara"
              className={`${dutyStatusColor} rounded-lg w-10 h-10 flex items-center justify-center hover:opacity-80 transition-opacity`}
            >
              <span className="text-white font-bold text-sm">
                {dutyStatusAbbr}
              </span>
            </a>
          ) : (
            <div
              className={`${dutyStatusColor} rounded-lg w-10 h-10 flex items-center justify-center`}
            >
              <span className="text-white font-bold text-sm">
                {dutyStatusAbbr}
              </span>
            </div>
          )}
          <span className="text-[0.625rem] text-muted mt-0.5">
            {getDutyStatusLabel(currentDutyStatus)}
          </span>
        </div>
      )}

      {/* Circular Progress Indicators */}
      {breakTimeRemainingMins !== null && (
        <CircularProgress
          value={breakTimeRemainingMins}
          max={MAX_VALUES.break}
          label="Break"
          timeDisplay={formatMinutesToTime(breakTimeRemainingMins)}
        />
      )}

      {driveTimeRemainingMins !== null && (
        <CircularProgress
          value={driveTimeRemainingMins}
          max={MAX_VALUES.drive}
          label="Drive"
          timeDisplay={formatMinutesToTime(driveTimeRemainingMins)}
        />
      )}

      {shiftTimeRemainingMins !== null && (
        <CircularProgress
          value={shiftTimeRemainingMins}
          max={MAX_VALUES.shift}
          label="Shift"
          timeDisplay={formatMinutesToTime(shiftTimeRemainingMins)}
        />
      )}

      {cycleTimeRemainingMins !== null && (
        <CircularProgress
          value={cycleTimeRemainingMins}
          max={MAX_VALUES.cycle}
          label="Cycle"
          timeDisplay={formatMinutesToTime(cycleTimeRemainingMins)}
        />
      )}
    </div>
  );
};

export default CompactHosWidget;
